import { Combobox } from "@headlessui/react";
import { LanguageIcon } from "@heroicons/react/20/solid";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  getTranslationByKey,
  searchTranslations,
} from "../services/translationCrudService";
import TranslationKeyModal from "./TranslationKeyModal";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function TranslationKeySelect({ value, onChange }) {
  const { token } = useSelector((state) => state.user);
  const [results, setResults] = useState([]);
  const debounceRef = useRef(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [modalTranslations, setModalTranslations] = useState([]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.trim() === "") {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const response = await searchTranslations(
        `${process.env.REACT_APP_HOST_BASE_URL}/v1/translation/search?key=${encodeURIComponent(value)}`,
        token,
      );
      if (response.success) {
        setResults(response.data || []);
      }
    }, 500);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleOpenModal = async () => {
    const response = await getTranslationByKey(
      `${process.env.REACT_APP_HOST_BASE_URL}/v1/translation/by-key/${encodeURIComponent(value)}`,
      token,
    );
    const found =
      response.success &&
      Array.isArray(response.data?.translations) &&
      response.data.translations.length > 0;
    if (found) {
      setModalMode("view");
      setModalTranslations(response.data.translations);
    } else {
      setModalMode("create");
      setModalTranslations([]);
    }
    setModalOpen(true);
  };

  const trimmedValue = (value || "").trim();
  const isEmpty = trimmedValue === "";
  const hasMatch =
    !isEmpty && results.some((item) => item.key === trimmedValue);

  return (
    <Combobox as="div" className="relative" value={value} onChange={onChange}>
      <div className="flex items-stretch bg-white border border-gray-300 rounded-md shadow-sm focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500">
        <Combobox.Input
          className="flex-1 min-w-0 py-2 pl-3 pr-2 bg-transparent border-0 rounded-l-md focus:outline-none focus:ring-0 sm:text-sm"
          onChange={(e) => onChange(e.target.value)}
          displayValue={(key) => key || ""}
          placeholder="Kalit qidirish yoki yozish..."
          autoComplete="off"
        />
        <button
          type="button"
          disabled={isEmpty}
          onClick={handleOpenModal}
          className={classNames(
            "flex items-center justify-center px-3 border-l border-gray-300 rounded-r-md focus:outline-none",
            isEmpty
              ? "text-gray-400"
              : hasMatch
                ? "text-green-600 hover:text-green-700"
                : "text-red-500 hover:text-red-600",
          )}
          aria-label="Tarjimani ko'rish yoki yaratish"
        >
          <LanguageIcon className="w-5 h-5" />
        </button>
      </div>
      {results.length > 0 && (
        <Combobox.Options className="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-48 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {results.map((item) => {
            const uzValue = item.translations?.find(
              (t) => t.code === "uz",
            )?.value;
            return (
              <Combobox.Option
                key={item.key}
                value={item.key}
                className={({ active }) =>
                  classNames(
                    "relative cursor-default select-none py-2 pl-3 pr-9",
                    active ? "bg-indigo-600 text-white" : "text-gray-900",
                  )
                }
              >
                {({ active }) => (
                  <>
                    <span className="block font-medium truncate">
                      {item.key}
                    </span>
                    {uzValue && (
                      <span
                        className={classNames(
                          "block text-xs truncate",
                          active ? "text-indigo-200" : "text-gray-500",
                        )}
                      >
                        {uzValue}
                      </span>
                    )}
                  </>
                )}
              </Combobox.Option>
            );
          })}
        </Combobox.Options>
      )}
      <TranslationKeyModal
        open={modalOpen}
        setOpen={setModalOpen}
        translationKey={value}
        mode={modalMode}
        existingTranslations={modalTranslations}
        onCreated={(key) => onChange(key)}
      />
    </Combobox>
  );
}
